import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as styles from './styles';
import styled from 'styled-components';
import { OtpViewProps } from './models';
import { useTranslation } from '@sunbit-dev/translations';
import { Slogan } from '../slogan/Slogan';
import { useTrack } from '~/hooks/track';
import { EventsNames } from '~/enums/Events';
import { PinCodeMethod } from '@sunbit-dev/purchase-common/src/models/PinCodeMethod';
import { ButtonSize, PhoneNumber, phoneRifmRecipe, RadioGroup, StyledLink } from '@sunbit-dev/web-ui-lib';
import { ActionButton } from '@sunbit-dev/purchase-common/src/components/actionButton/ActionButton';
import URLs, { InfoURLs } from '~/enums/URLs';
import { useHistory, useLocation } from 'react-router-dom';
import { stringToPhoneString } from '@sunbit-dev/purchase-common/src/utilities/string';
import { PrivacyOptions } from '@sunbit-dev/common/enums/Datadog';
import { PrivacyAllowanceLayer } from '@sunbit-dev/purchase-common/src/components/privacyAllowanceLayer/PrivacyAllowanceLayer';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService';
import { addError } from '~/store/reducers/errors/errors';
import { useDispatch, useSelector } from 'react-redux';
import { LinksTestIds, PagesTestIds } from '~/enums/TestIds';
import { NetworkServiceError, TrackCategory } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import { ErrorCodes } from '~/enums/Errors';
import { Legal } from '~/components/app/components/landingPage/components/legal/Legal';
import { useMediaQuery } from 'react-responsive';
import { Size } from '@sunbit-dev/purchase-common/src/theme/defaultTheme';
import infoImage from '~/assets/info-outline-light.svg';
import messageService from '~/services/MessageService';
import PlaceInFlowPages from '~/services/MessageService/PlaceInFlowPages';
import { LoanApplicationOrigin } from '~/enums/LoanApplicationOrigin';
import * as purchaseStore from '~/store/reducers/purchase/purchase';
import { getTermByVerticalType } from '~/utils/getTermByVerticalType';
import { isEmbeddedMode } from '@sunbit-dev/purchase-common/src/helpers/embeddedMode';
import { formatDisplayFirstName } from '~/helpers/utils';

export const OtpView = styled(({ className, onPinCodeMethodSubmit, isPhoneNumberRequired }: OtpViewProps) => {
  const [pinCodeMethod, setPinCodeMethod] = useState<PinCodeMethod>(PinCodeMethod.SMS);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState(undefined);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [observer, setObserver] = useState<IntersectionObserver>(null);

  const { t } = useTranslation('landing_page', { keyPrefix: 'otp_view' });
  const { track } = useTrack();
  const history = useHistory();
  const { search } = useLocation();
  const dispatch = useDispatch();
  const { isPhoneValid } = phoneRifmRecipe;
  const { purchaseInfo } = useSelector(purchaseStore.getStore);

  const purchaseTerm = getTermByVerticalType('purchaseTerm', purchaseInfo?.isHealthcareVertical);

  const rootDiv = useRef<HTMLDivElement | undefined>(undefined);
  const legalContentEnd = useRef<HTMLDivElement | undefined>(undefined);

  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(false);
  const [shouldDisplayLegalScrollNotice, setShouldDisplayLegalScrollNotice] = useState(false);

  const radioOptions = [
    { label: t('pin_code.text_label'), dataTestId: 'pincode-method-sms-radio', value: PinCodeMethod.SMS },
    { label: t('pin_code.call_label'), dataTestId: 'pincode-method-call-radio', value: PinCodeMethod.CALL },
  ];

  const isMobile = useMediaQuery({
    query: `(max-width: ${Size.mobileXL}px)`,
  });

  useEffect(() => {
    track({ eventName: EventsNames.otpView, type: TrackCategory.View });
  }, []);

  useEffect(() => {
    if (purchaseInfo?.loanApplicationOrigin === LoanApplicationOrigin.ONLINE_SDK) {
      messageService.messagePlaceInFlow(PlaceInFlowPages.PHONE_NUMBER, null);
    }
  }, [purchaseInfo]);

  useEffect(() => {
    if (hasReachedBottom) setShouldDisplayLegalScrollNotice(false);

    if (hasReachedBottom && isScrollListenerActive) {
      setIsScrollListenerActive(false);
      if (observer) {
        observer.disconnect();
      }
    }

    if (isMobile && rootDiv.current && legalContentEnd.current && !isScrollListenerActive && !observer) {
      setIsScrollListenerActive(true);
      const newObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setHasReachedBottom(true);
          }
        },
        { threshold: 1 }
      );
      setObserver(newObserver); // Set the observer
      newObserver.observe(legalContentEnd.current); // Call observe on the new observer
    }

    // Cleanup function to ensure observer is disconnected when unmounting
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [isMobile, hasReachedBottom, isScrollListenerActive, observer]);

  const onSubmitClick = useCallback(() => {
    if (isMobile && !hasReachedBottom) {
      setShouldDisplayLegalScrollNotice(true);
    }
  }, [hasReachedBottom, isMobile]);

  const onPinCodeMethodConfirm = useCallback(async () => {
    const eventName = EventsNames.sendPinCode;
    if (isPhoneNumberRequired && !phoneNumber) {
      setPhoneNumberError(t('input_label'));
    } else {
      try {
        setDisableSubmit(true);
        await loanApplicationNetworkService.sendPinCode({
          pinCodeMethod,
          eventName,
          ...(phoneNumber && { phoneNumber }),
        });
        onPinCodeMethodSubmit(pinCodeMethod, phoneNumber);
      } catch (error: any) {
        let responseData = (error as NetworkServiceError)?.responseData;
        if (responseData?.code === ErrorCodes.NonUSPhone || responseData?.code === ErrorCodes.NumberParseError) {
          setPhoneNumberError(t('errors.non_us_phone_number'));
        } else dispatch(addError({ eventName, payload: error }));
      } finally {
        setDisableSubmit(false);
      }
    }
  }, [onPinCodeMethodSubmit, pinCodeMethod, setDisableSubmit, phoneNumber, setPhoneNumberError]);

  const methodTypeText = useMemo<{ methodType: string; chargeType: string }>(
    () =>
      pinCodeMethod === PinCodeMethod.SMS
        ? t('agreement.first_paragraph.part_1.sms_method')
        : t('agreement.first_paragraph.part_1.call_method'),
    [pinCodeMethod]
  );

  const convertElementsArrayToFragments = (ElementsArray): React.ReactFragment[] => {
    return ElementsArray.map((element, index) => <React.Fragment key={index}>{element}</React.Fragment>);
  };

  const toInfoLink = ({ text, dataTestId, infoUrl }: { text: string; dataTestId: string; infoUrl: InfoURLs }) => {
    const onClick = () => history.replace({ pathname: [URLs.Info, infoUrl].join(''), search });
    return (
      <StyledLink className="link" data-testid={dataTestId} onClick={onClick}>
        {text}
      </StyledLink>
    );
  };

  const agreementFirstParagraph = convertElementsArrayToFragments([
    methodTypeText,
    toInfoLink({
      text: t('agreement.first_paragraph.part_2'),
      dataTestId: LinksTestIds.TermsAndConditions,
      infoUrl: InfoURLs.TermsAndConditions,
    }),
  ]);

  const onPhoneInputChange = useCallback(
    (newInput: string) => {
      setPhoneNumber(newInput);
      setPhoneNumberError(!isPhoneValid(newInput) ? t('errors.invalid_phone_number') : undefined);
    },
    [setPhoneNumber, setPhoneNumberError]
  );

  const actionButton = useMemo(() => {
    const isDisableForLegalText = isMobile && !hasReachedBottom;

    const button = (
      <ActionButton
        dataDdAction={EventsNames.submitPage}
        className="submit-button"
        dataTestId="submit-button"
        size={ButtonSize.small}
        buttonText={t('submit_button')}
        submit={onPinCodeMethodConfirm}
        disabled={
          disableSubmit || isDisableForLegalText || (isPhoneNumberRequired && (phoneNumberError || !phoneNumber))
        }
      />
    );

    return isDisableForLegalText ? (
      <div className="submit-button-container" onClick={onSubmitClick}>
        {button}
        <div className="overlay"></div>
      </div>
    ) : (
      button
    );
  }, [
    pinCodeMethod,
    disableSubmit,
    phoneNumberError,
    phoneNumber,
    isMobile,
    hasReachedBottom,
    isPhoneNumberRequired,
    onPinCodeMethodConfirm,
  ]);

  const [part_1, part_2, part_3, part_4, part_5, part_6, part_7] = t('agreement.second_paragraph');

  const agreementSecondParagraph = convertElementsArrayToFragments([
    part_1,
    toInfoLink({
      text: part_2,
      dataTestId: LinksTestIds.ArbitrationProvision,
      infoUrl: InfoURLs.ArbitrationProvision,
    }),
    part_3,
    toInfoLink({ text: part_4, dataTestId: LinksTestIds.PrivacyPolicy, infoUrl: InfoURLs.PrivacyPolicy }),
    part_5,
    toInfoLink({ text: part_6, dataTestId: LinksTestIds.TermsOfUse, infoUrl: InfoURLs.TermsOfUse }),
    part_7,
  ]);

  const [part1, part2, part3, part4, part5] = t('healthcare');

  const healthcareParagraph = convertElementsArrayToFragments([
    part1,
    toInfoLink({ text: part2, dataTestId: LinksTestIds.PrivacyPolicy, infoUrl: InfoURLs.PrivacyPolicy }),
    part3,
    isEmbeddedMode() ? part4 : <StyledLink href="mailto:records@sunbit.com">{part4}</StyledLink>,
    part5,
  ]);

  const getTopSectionWithKnownPhone = () => (
    <>
      <PrivacyAllowanceLayer privacyLevel={PrivacyOptions.Hidden}>
        <h1 className="title">
          {purchaseInfo?.userFirstName
            ? t('welcome_with_name', {
                userFirstName: formatDisplayFirstName(purchaseInfo.userFirstName),
              })
            : t('welcome')}
          {t('title_with_phone', {
            userPhoneNumber: stringToPhoneString(purchaseInfo?.userMaskedPhone),
          })}
        </h1>
      </PrivacyAllowanceLayer>
      <h2 className="sub-title">{t('sub_title')}</h2>
    </>
  );

  const getTopSectionWithPhoneInput = () => (
    <>
      <h1 className="title">
        {t('title_without_phone', {
          merchantName: purchaseInfo?.merchantName,
          purchaseTerm,
        })}
        {purchaseInfo?.amountRequested && t('amount_part', { purchaseAmount: purchaseInfo?.amountRequested })}
      </h1>

      <div className="phone-number-input-section">
        <label className="phone-number-input-label">{t('input_label')}</label>
        <PrivacyAllowanceLayer privacyLevel={PrivacyOptions.MaskUserInput}>
          <PhoneNumber
            className="phone-number-container"
            dataTestId="phone-number-input"
            name="phoneNumber"
            onChange={onPhoneInputChange}
            value={phoneNumber}
            error={phoneNumberError}
            placeholder={t('phone_number_placeholder')}
            withNumericMobileKeyboard={true}
          />
        </PrivacyAllowanceLayer>
      </div>
    </>
  );

  return (
    <div className={className} data-testid={PagesTestIds.OtpView} ref={rootDiv}>
      <PrivacyAllowanceLayer>
        <Slogan className="slogan" />

        {isPhoneNumberRequired ? getTopSectionWithPhoneInput() : getTopSectionWithKnownPhone()}

        <div className="pin-code-methods-container">
          <RadioGroup
            color="primary"
            name="pinCodeMethodForm"
            direction="row"
            value={pinCodeMethod}
            radioOptions={radioOptions}
            onChange={setPinCodeMethod}
            dataTestId="pin-code-method"
          />
        </div>

        <div className="legals-container">
          {pinCodeMethod && (
            <>
              <p className="first-paragraph">{agreementFirstParagraph}</p>
              <p className="second-paragraph">{agreementSecondParagraph}</p>
              {isMobile && <div className="sticky-button-wrapper-placeholder" />}
            </>
          )}
        </div>

        {purchaseInfo?.isHealthcareVertical && <p className="healthcare-paragraph">{healthcareParagraph}</p>}

        {!isMobile && <div className="button-wrapper">{actionButton}</div>}

        <Legal className="legal-content" showAsterisk={isPhoneNumberRequired} />

        <div ref={legalContentEnd} className="legal-content-end"></div>

        {isMobile && (
          <>
            <div className="mobile-button-container">
              {shouldDisplayLegalScrollNotice && (
                <div className="scroll-notice">
                  <img className="info-icon" src={infoImage} alt={t('info')} />
                  Scroll to the bottom of the screen
                </div>
              )}
              <div className="sticky-button-wrapper">{actionButton}</div>
            </div>
            <div className="sticky-button-wrapper-placeholder" />
          </>
        )}
      </PrivacyAllowanceLayer>
    </div>
  );
})`
  ${styles.OtpView}
`;
